import { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { usePageActions } from '../contexts/PageActionsContext';
import { templateApiService } from '../services/templateApiService';
import { OfficialApiInbox } from '../types/template';
import { TemplateInboxList } from '../components/TemplateInboxList';
import { TemplateList } from '../components/TemplateList';
import toast from 'react-hot-toast';

export function TemplatesPage() {
  const { theme } = useTheme();
  const { isUrlLogin } = useAuth();
  const { selectedTenantId } = useTenant();
  const { registerMetadata } = usePageActions();
  const [inboxes, setInboxes] = useState<OfficialApiInbox[]>([]);
  const [selectedInbox, setSelectedInbox] = useState<OfficialApiInbox | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setSelectedInbox(null);
    loadInboxes();
  }, [selectedTenantId]);

  // Listen for back navigation from sidebar icon
  useEffect(() => {
    const handleBack = () => {
      if (selectedInbox) {
        setSelectedInbox(null);
      }
    };

    window.addEventListener('templates:back', handleBack);
    return () => {
      window.removeEventListener('templates:back', handleBack);
    };
  }, [selectedInbox]);

  // Register metadata for iframe mode and navigation icon
  useEffect(() => {
    registerMetadata('/templates', {
      title: selectedInbox ? `Templates - ${selectedInbox.name}` : 'Templates',
      subtitle: selectedInbox 
        ? `${selectedInbox.phoneNumber}` 
        : 'API Oficial do WhatsApp',
      showBackIcon: !!selectedInbox // Show back icon when viewing an inbox
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInbox]);

  const loadInboxes = async () => {
    try {
      setLoading(true);
      const data = await templateApiService.getInboxes();
      setInboxes(data);
    } catch (error: any) {
      console.error('❌ Erro ao carregar caixas:', error);
      // Tratar erros de "sem caixa" como lista vazia, não como erro
      const errorMsg = error?.message || '';
      const isNoInboxError = errorMsg.includes('canais.filter') 
        || errorMsg.includes('Nenhuma caixa')
        || errorMsg.includes('Tenant sem plataforma configurada');
      
      if (!isNoInboxError) {
        toast.error(errorMsg || 'Erro ao carregar caixas de entrada');
      }
      setInboxes([]); // Garantir estado limpo para mostrar empty state
    } finally {
      setLoading(false);
    }
  };

  const handleSelectInbox = (inbox: OfficialApiInbox) => {
    // console.log('📦 Caixa selecionada:', inbox.name);
    setSelectedInbox(inbox);
  };

  const handleBack = () => {
    // console.log('🔙 Voltando para lista de caixas');
    setSelectedInbox(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{
        backgroundColor: theme === 'dark' ? '#1A1A1A' : '#F3F4F6'
      }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}>
            Carregando caixas de entrada...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{
      backgroundColor: theme === 'dark' ? '#1A1A1A' : '#F3F4F6'
    }}>
      {/* Header - Only show in normal mode */}
      {!isUrlLogin && (
      <div className={`shadow-sm ${theme === 'dark' ? 'bg-[#2A2A2A]' : 'bg-white'}`}>
        <div className="container mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                {selectedInbox ? (
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleBack}
                      className={`p-2 rounded-lg transition-colors ${
                        theme === 'dark'
                          ? 'hover:bg-gray-700 text-gray-300'
                          : 'hover:bg-gray-100 text-gray-600'
                      }`}
                      title="Voltar"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <span>Templates - {selectedInbox.name}</span>
                  </div>
                ) : (
                  'Gerenciamento de Templates'
                )}
              </h1>
              <p className={`text-sm font-normal ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                {selectedInbox ? (
                  `Gerenciar templates do número ${selectedInbox.phoneNumber}`
                ) : (
                  'Gerencie templates da API Oficial do WhatsApp'
                )}
              </p>
            </div>

            {selectedInbox && (
              <div className={`px-4 py-2 rounded-lg ${
                theme === 'dark' ? 'bg-gray-700' : 'bg-blue-50'
              }`}>
                <p className={`text-sm font-medium ${
                  theme === 'dark' ? 'text-gray-300' : 'text-blue-900'
                }`}>
                  📱 {selectedInbox.phoneNumber}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      )}

      {/* Content */}
      <div className="container mx-auto px-6 py-8">
        {!selectedInbox ? (
          <TemplateInboxList
            inboxes={inboxes}
            onSelectInbox={handleSelectInbox}
            onRefresh={loadInboxes}
          />
        ) : (
          <TemplateList
            inbox={selectedInbox}
            onBack={handleBack}
          />
        )}
      </div>
    </div>
  );
}

